'use es6';

import { List, Map as ImmutableMap } from 'immutable';
import keyMirror from 'react-utils/keyMirror';
import partial from 'transmute/partial';
import translate from 'transmute/translate';
import { ENABLED } from 'customer-data-email/schema/connectedAccount/ConnectedAccountFeatureStates';
import { GMAIL, OUTLOOK365 } from 'customer-data-email/schema/connectedAccount/EmailClientTypes';
import { IMAP } from 'customer-data-email/schema/connectedAccount/EmailIntegrationTypes';
import { ALIAS, FACSIMILE_INBOX, CRM_EMAIL_INTEGRATION } from 'customer-data-email/schema/connectedAccount/ConnectedAccountFeatureTypes';
import EmailClients from 'customer-data-email/schema/connectedAccount/EmailClients';
import EmailIntegration from 'customer-data-email/schema/connectedAccount/EmailIntegration';
import EmailAliasList from 'customer-data-email/schema/connectedAccount/EmailAliasList';
import EmailAlias from 'customer-data-email/schema/connectedAccount/EmailAlias';
import ConnectedAccount from 'customer-data-email/schema/connectedAccount/ConnectedAccount';
const KEYS = keyMirror({
  accountId: null,
  aliases: null,
  emailProviderType: null,
  featureType: null,
  features: null,
  state: null
});
const EmailClientTypes = {
  GMAIL,
  OUTLOOK365
};
const emailClientsByProviderType = ImmutableMap(EmailClientTypes).reduce((clientsByProviderType, clientType) => {
  const {
    providerTypes
  } = EmailClients[clientType];
  return providerTypes.reduce((reduction, providerType) => {
    return reduction.set(providerType, clientType);
  }, clientsByProviderType);
}, ImmutableMap());
const isEnabled = feature => feature.get(KEYS.state) === ENABLED;
const getIntegrationType = emailProviderType => {
  let integrationType = emailClientsByProviderType.get(emailProviderType);
  if (!integrationType) {
    integrationType = IMAP;
  }
  return integrationType;
};
const emailIntegrationTranslation = {
  type: int => getIntegrationType(int.get(KEYS.emailProviderType)),
  state: true
};
const aliasTranslation = {
  address: alias => alias.get('email').toLowerCase(),
  displayableAddress: 'email',
  primary: true,
  signature: true,
  default: true,
  type: true
};
const toAliases = aliasList => {
  const aliases = isEnabled(aliasList) && aliasList.has(KEYS.aliases) ? aliasList.get(KEYS.aliases) : List();
  return aliases.map(alias => EmailAlias(translate(aliasTranslation, alias)));
};
const aliasListTranslation = {
  aliases: toAliases,
  state: true
};
const extractFeature = (featureType, account) => {
  return account.get(KEYS.features).find(feature => feature.get(KEYS.featureType) === featureType);
};
const extractFeatureState = (featureType, account) => {
  const feature = extractFeature(featureType, account);
  return feature ? feature.get(KEYS.state) : null;
};
const extractIntegration = account => {
  let integration = extractFeature(CRM_EMAIL_INTEGRATION, account);
  if (integration) {
    integration = translate(emailIntegrationTranslation, integration);
  }
  return new EmailIntegration(integration);
};
const extractAliasList = account => {
  let aliasList = extractFeature(ALIAS, account);
  if (aliasList) {
    aliasList = translate(aliasListTranslation, aliasList);
  }
  return EmailAliasList(aliasList);
};
const extractIsSharedFlag = account => {
  const fascimileFeature = extractFeature(FACSIMILE_INBOX, account);
  if (fascimileFeature && fascimileFeature.has('inbox')) {
    const inbox = fascimileFeature.get('inbox');
    return !!(inbox && inbox.get('shared'));
  }
  return false;
};
const extractInboxType = account => {
  const fascimileFeature = extractFeature(FACSIMILE_INBOX, account);
  let inbox;
  if (fascimileFeature && fascimileFeature.has('inbox')) {
    inbox = fascimileFeature.get('inbox');
  }
  return inbox && inbox.get('inboxType');
};
const connectedAccountTranslation = {
  address: acct => acct.get(KEYS.accountId).toLowerCase(),
  facsimileInbox: partial(extractFeatureState, FACSIMILE_INBOX),
  integration: extractIntegration,
  aliasList: extractAliasList,
  shared: extractIsSharedFlag,
  inboxType: extractInboxType
};
const toConnectedAccount = account => {
  return new ConnectedAccount(translate(connectedAccountTranslation, account));
};
const accountHasId = acct => !!acct.get(KEYS.accountId);
export function toConnectedAccounts(accountsList) {
  return accountsList.filter(accountHasId).map(toConnectedAccount);
}